import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Paper,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Snackbar,
  Alert,
  DialogContentText,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Timer as TimerIcon,
  Euro as EuroIcon,
  Build as BuildIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../../utils/api';
import { openWhatsAppWithTemplate, TEMPLATE_TYPES, openMaintenanceReviewWhatsApp, openWhatsAppChat } from '../../utils/whatsappTemplates';
import MaintenanceServiceSheet from './MaintenanceServiceSheet';
import MaintenanceStandardPrint from './MaintenanceStandardPrint';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Map maintenance status to appropriate WhatsApp template type
const statusToTemplateType = {
  // Stati senza messaggio (testo vuoto)
  'In Coda': null,
  'In Corso': null,
  'Attesa Ricambi': null,
  // Messaggi standard
  'Attesa Cliente': TEMPLATE_TYPES.ESTIMATE_READY, // "preventivo"
  'Da Chiamare': TEMPLATE_TYPES.MAINTENANCE_COMPLETION, // "completamento"
  'Da Ritirare': TEMPLATE_TYPES.PICKUP_REMINDER, // "sollecito"
  'Sollecitato': TEMPLATE_TYPES.PICKUP_REMINDER, // "sollecito"
  'Parcheggio': TEMPLATE_TYPES.PICKUP_REMINDER, // "sollecito"
  'Consegnato': TEMPLATE_TYPES.MAINTENANCE_REVIEW // "recensione"
};

const MaintenanceDetails = ({ open, handleClose, onClose, maintenanceId, laborCost: externalLaborCost }) => {
  // Support both handleClose and onClose props for backward compatibility
  const closeDialog = handleClose || onClose;
  const [loading, setLoading] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceItems, setMaintenanceItems] = useState([]);
  const [workSessions, setWorkSessions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', action: '' });
  
  // Nuovo stato per il dialog dell'acconto
  const [depositDialog, setDepositDialog] = useState({ open: false });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    paymentMethodId: '',
    accountId: '',
    paymentDate: new Date().toISOString().split('T')[0]
  });
  
  // Stati per la stampa
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [selectedPrintType, setSelectedPrintType] = useState(null);
  
  // Stati per la stampa preventivo
  const [quoteSelectionDialogOpen, setQuoteSelectionDialogOpen] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState(null);
  const [openMaintenanceDetails, setOpenMaintenanceDetails] = useState(false);

  useEffect(() => {
    if (open && maintenanceId) {
      fetchMaintenance();
      fetchMaintenanceItems();
      fetchWorkSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maintenanceId]);

  const fetchMaintenance = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/maintenance/${maintenanceId}`);
      setMaintenance(res.data);
    } catch (err) {
      console.error('Error fetching maintenance:', err);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per caricare lo storico manutenzioni del cliente
  const fetchMaintenanceHistory = async (customerId) => {
    if (!customerId) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/maintenance/customer/${customerId}`);
      const history = res.data || [];
      // Ordina per data (più recente per prima)
      const sortedHistory = history.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.startDate || 0);
        const dateB = new Date(b.createdAt || b.startDate || 0);
        return dateB - dateA; // Ordine decrescente (più recente per prima)
      });
      setMaintenanceHistory(sortedHistory);
    } catch (err) {
      console.error('Error fetching maintenance history:', err);
      setMaintenanceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Funzione per aprire il dialog dello storico manutenzioni
  const handleOpenHistoryDialog = () => {
    if (maintenance?.Customer?.id) {
      fetchMaintenanceHistory(maintenance.Customer.id);
      setOpenHistoryDialog(true);
    }
  };

  // Funzione per chiudere il dialog dello storico manutenzioni
  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
    setMaintenanceHistory([]);
  };

  // Funzione per gestire il click su una manutenzione nello storico
  const handleMaintenanceClick = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setOpenMaintenanceDetails(true);
  };

  // Funzione per chiudere il dialog dei dettagli manutenzione
  const handleCloseMaintenanceDetails = () => {
    setOpenMaintenanceDetails(false);
    setSelectedMaintenanceId(null);
  };

  // Funzione per formattare la data
  const formatDateHistory = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data non valida';
      }
      return format(date, 'dd/MM/yyyy', { locale: it });
    } catch (error) {
      return 'Data non valida';
    }
  };

  // Funzione per ottenere il colore dello stato
  const getStatusColorHistory = (status) => {
    const statusColors = {
      'In Coda': 'default',
      'In Corso': 'primary',
      'Attesa Ricambi': 'warning',
      'Attesa Cliente': 'info',
      'Da Chiamare': 'secondary',
      'Da Ritirare': 'success',
      'Sollecitato': 'error',
      'Parcheggio': 'default',
      'Consegnato': 'success'
    };
    return statusColors[status] || 'default';
  };

  const fetchMaintenanceItems = async () => {
    try {
      const res = await api.get(`/maintenance-items/maintenance/${maintenanceId}`);
      setMaintenanceItems(res.data);
    } catch (err) {
      console.error('Error fetching maintenance items:', err);
    }
  };

  const fetchWorkSessions = async () => {
    try {
      const res = await api.get(`/work-sessions/maintenance/${maintenanceId}`);
      setWorkSessions(res.data);
    } catch (err) {
      console.error('Error fetching work sessions:', err);
    }
  };

  const fetchPaymentData = async () => {
    try {
      const [paymentMethodsRes, accountsRes] = await Promise.all([
        api.get('/payment-methods'),
        api.get('/accounts')
      ]);
      setPaymentMethods(paymentMethodsRes.data);
      setAccounts(accountsRes.data);
    } catch (err) {
      console.error('Error fetching payment data:', err);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento dei dati di pagamento',
        severity: 'error'
      });
    }
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculateTotalCost = () => {
    // Calcola il totale dei prodotti NON di manodopera
    console.log('📦 maintenanceItems in calculateTotalCost:', maintenanceItems);
    if (!Array.isArray(maintenanceItems)) {
      console.warn('⚠️ maintenanceItems non è un array in calculateTotalCost');
      return 0;
    }
    return maintenanceItems.reduce((total, item) => {
      if (!item.isLabor) {
        return total + (parseFloat(item.unitPrice) * item.quantity);
      }
      return total;
    }, 0);
  };

  const calculateInternalLaborCost = () => {
    // Se maintenance non è ancora caricato, ritorna 0
    if (!maintenance) return 0;

    // Se la manutenzione è consegnata, usa il valore salvato
    if (maintenance.status === 'Consegnato' && maintenance.laborCost !== undefined) {
      return parseFloat(maintenance.laborCost);
    }

    if (!workSessions || workSessions.length === 0) {
      return 0;
    }
    
    // Calcola il costo totale sommando il total_cost di tutte le sessioni
    const totalCost = workSessions.reduce((total, session) => {
      const sessionCost = parseFloat(session.total_cost || session.totalCost || 0);
      return total + sessionCost;
    }, 0);
    
    return totalCost;
  };

  const calculateMaterialsCost = () => {
    // Se maintenance non è ancora caricato, ritorna 0
    if (!maintenance) return 0;

    // Se la manutenzione è consegnata, usa il valore salvato + IVA 22%
    if (maintenance.status === 'Consegnato' && maintenance.materialsCost !== undefined) {
      return parseFloat(maintenance.materialsCost) * 1.22;
    }

    // Somma il costo di tutti i materiali (esclusa manodopera), usando customPurchasePrice se presente, altrimenti purchasePrice del prodotto, il tutto + IVA 22%
    if (!Array.isArray(maintenanceItems)) {
      return 0;
    }
    return maintenanceItems.reduce((total, item) => {
      if (!item.isLabor) {
        if (item.customPurchasePrice != null) {
          return total + (parseFloat(item.customPurchasePrice) * item.quantity * 1.22);
        }
        if (item.Product && item.Product.purchasePrice != null) {
          return total + (parseFloat(item.Product.purchasePrice) * item.quantity * 1.22);
        }
      }
      return total;
    }, 0);
  };
  
  const calculateOverheadCost = () => {
    // Se maintenance non è ancora caricato, ritorna 0
    if (!maintenance) return 0;

    // Se la manutenzione è consegnata, usa il valore salvato + IVA 22%
    if (maintenance.status === 'Consegnato' && maintenance.overheadCost !== undefined) {
      return parseFloat(maintenance.overheadCost) * 1.22;
    }

    // Calcola il 20% di laborCost + materialsCost (senza aggiungere nuovamente l'IVA)
    const materialsCost = calculateMaterialsCost();
    return (laborCost + materialsCost) * 0.2;
  };
  
  const calculateGrandTotal = () => {
    // Se maintenance non è ancora caricato, ritorna 0
    if (!maintenance) return 0;

    // Se la manutenzione è consegnata, usa il valore salvato + IVA 22%
    if (maintenance.status === 'Consegnato' && maintenance.totalCost !== undefined) {
      return parseFloat(maintenance.totalCost) * 1.22;
    }

    // Somma laborCost + materialsCost + overheadCost
    const materialsCost = calculateMaterialsCost();
    const overheadCost = calculateOverheadCost();
    return laborCost + materialsCost + overheadCost;
  };

  const calculateTotalLaborTime = () => {
    console.log('📦 Calcolo tempo totale manodopera');
    
    // Verifica che workSessions sia un array valido
    if (!workSessions || !Array.isArray(workSessions)) {
      console.warn('⚠️ workSessions non è un array valido');
      return 0;
    }
    
    // Utilizza direttamente i valori duration dal database
    const totalSeconds = workSessions.reduce((total, session) => {
      // Usa il valore duration dalla sessione
      const sessionDuration = session.duration || 0;
      console.log(`Sessione ${session.id}: durata ${sessionDuration} sec`);
      return total + sessionDuration;
    }, 0);
    
    console.log('⏱️ Tempo totale manodopera:', totalSeconds, 'secondi');
    return totalSeconds;
  };

  // Use external labor cost if provided, otherwise calculate it internally
  const laborCost = externalLaborCost !== undefined ? externalLaborCost : calculateInternalLaborCost();

  const handleCompleteMaintenance = async () => {
    setLoading(true);
    try {
      const res = await api.put(`/maintenance/${maintenanceId}`, {
        ...maintenance,
        status: 'Da Chiamare'
      });
      setMaintenance(res.data);
      try {
        await openMaintenanceReviewWhatsApp(res.data);
      } catch (e) {
        console.error('WhatsApp recensioni dopo completamento manutenzione:', e);
      }
      setSnackbar({
        open: true,
        message: 'Manutenzione completata con successo! Stato aggiornato a "Da Chiamare"',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error completing maintenance:', err);
      setSnackbar({
        open: true,
        message: `Errore durante il completamento della manutenzione: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReopenMaintenance = async () => {
    setLoading(true);
    try {
      const res = await api.put(`/maintenance/${maintenanceId}`, {
        ...maintenance,
        status: 'In Corso'
      });
      setMaintenance(res.data);
      setSnackbar({
        open: true,
        message: 'Manutenzione riaperta con successo! Stato aggiornato a "In Corso"',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error reopening maintenance:', err);
      setSnackbar({
        open: true,
        message: `Errore durante la riapertura della manutenzione: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!maintenance || !maintenance.Customer) return;
    
    try {
      // Recupera le impostazioni dell'azienda per ottenere il termsAndConditions URL
      const companyId = localStorage.getItem('companyId');
      if (!companyId) {
        console.error('CompanyId non trovato in localStorage');
        return;
      }
      
      // Prima otteniamo le company-settings
      const companySettingsResponse = await api.get('/company-settings', {
        headers: { 'x-company-id': companyId }
      });
      
      // Poi otteniamo le settings generali che contengono termsAndConditions
      const generalSettingsResponse = await api.get('/settings');
      
      const companySettings = companySettingsResponse.data || {};
      const generalSettings = generalSettingsResponse.data || {};
      
      console.log('🔄 Impostazioni recuperate:', {
        company: companySettings,
        general: generalSettings
      });
      
      // Prepare data for template variables
      // Calcola il totale considerando lo sconto
      const finalCost = parseFloat(maintenance.finalCost || 0);
      const discount = parseFloat(maintenance.discount || 0);
      const totalAmountWithDiscount = finalCost - discount;
      
      // Calcola daysInShop per i messaggi di sollecito
      let daysInShop = 0;
      if (maintenance.status === 'Da Ritirare' || maintenance.status === 'Sollecitato') {
        // Per i messaggi di sollecito, usa la data del primo messaggio di ritiro se disponibile
        if (maintenance.firstPickupReminderSentAt) {
          const firstReminderDate = new Date(maintenance.firstPickupReminderSentAt);
          const now = new Date();
          const differenceInTime = now.getTime() - firstReminderDate.getTime();
          daysInShop = Math.max(0, Math.ceil(differenceInTime / (1000 * 3600 * 24)));
        } else if (maintenance.startDate) {
          // Fallback alla data di ingresso se non c'è la data del primo sollecito
          const startDate = new Date(maintenance.startDate);
          const now = new Date();
          const differenceInTime = now.getTime() - startDate.getTime();
          daysInShop = Math.max(0, Math.ceil(differenceInTime / (1000 * 3600 * 24)));
        }
      } else if (maintenance.startDate) {
        // Per gli altri messaggi, usa la data di ingresso
        const startDate = new Date(maintenance.startDate);
        const endDate = maintenance.endDate ? new Date(maintenance.endDate) : new Date();
        const differenceInTime = endDate.getTime() - startDate.getTime();
        daysInShop = Math.max(0, Math.ceil(differenceInTime / (1000 * 3600 * 24)));
      }
      
      const templateData = {
        customerName: maintenance.Customer.firstName,
        protocolNumber: maintenance.protocolNumber,
        vehicleModel: maintenance.Vehicle ? maintenance.Vehicle.model : '',
        problemDescription: maintenance.description,
        totalAmount: totalAmountWithDiscount > 0 ? totalAmountWithDiscount.toString() : (maintenance.estimatedCost || ''),
        estimateAmount: maintenance.estimatedCost || '',
        deposit: maintenance.deposit || '0',
        // Ottieni il link ai termini dalle impostazioni generali
        termsLink: generalSettings.termsAndConditions || 'https://www.officinabici.it/termini-e-condizioni',
        daysInShop: daysInShop.toString()
      };
      
      // Verifica i dati di template prima dell'invio
      console.log('Dati template WhatsApp:', templateData);
      
      // Determine which template to use based on maintenance status
      const templateType = statusToTemplateType[maintenance.status] || TEMPLATE_TYPES.MAINTENANCE_COMPLETION;

      // Stati senza messaggio: apri WhatsApp senza testo
      if (!statusToTemplateType[maintenance.status]) {
        const phone = maintenance.Customer?.phone;
        if (phone) {
          openWhatsAppChat(phone, maintenance.Customer?.countryCode);
        }
        return;
      }

      await openWhatsAppWithTemplate(
        maintenance.Customer.phone,
        templateType,
        templateData,
        maintenance.Customer.countryCode
      );
      
      // Mostra conferma SOLO per:
      // - Da Chiamare -> Da Ritirare
      // - Da Ritirare -> Sollecitato
      if (maintenance.status === 'Da Chiamare' || maintenance.status === 'Da Ritirare') {
        setTimeout(() => {
          setConfirmDialog({
            open: true,
            message: `Hai inviato correttamente il messaggio WhatsApp a ${maintenance.Customer.firstName} ${maintenance.Customer.lastName}?`,
            action: maintenance.status === 'Da Ritirare' ? 'updateToSollecitato' : 'updateToDaRitirare'
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Errore durante l\'invio del messaggio WhatsApp:', error);
      setSnackbar({
        open: true,
        message: 'Errore durante l\'invio del messaggio WhatsApp',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleConfirmWhatsApp = async (confirmed) => {
    setConfirmDialog({ ...confirmDialog, open: false });
    
    if (confirmed) {
      setLoading(true);
      try {
        // Get the template type that was used
        const templateType = statusToTemplateType[maintenance.status] || TEMPLATE_TYPES.MAINTENANCE_COMPLETION;
        
        // Determine the next status based on current status and template type
        let nextStatus = maintenance.status; // Default: keep current status
        
        // Cambia stato SOLO in questi casi:
        // - Da Chiamare (completamento) -> Da Ritirare
        // - Da Ritirare (sollecito) -> Sollecitato
        if (maintenance.status === 'Da Chiamare' && confirmDialog.action === 'updateToDaRitirare') {
          nextStatus = 'Da Ritirare';
        } else if (maintenance.status === 'Da Ritirare' && confirmDialog.action === 'updateToSollecitato') {
          nextStatus = 'Sollecitato';
        } else {
          // Negli altri casi non cambiamo stato
          setLoading(false);
          return;
        }
        
        const res = await api.put(`/maintenance/${maintenanceId}`, {
          ...maintenance,
          status: nextStatus,
          whatsappNotificationSent: true
        });
        setMaintenance(res.data);
        setSnackbar({
          open: true,
          message: `Stato aggiornato a "${nextStatus}" con successo!`,
          severity: 'success'
        });
      } catch (err) {
        console.error('Error updating maintenance status:', err);
        setSnackbar({
          open: true,
          message: `Errore durante l'aggiornamento dello stato: ${err.message}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteMaintenance = async () => {
    if (window.confirm('Sei sicuro di voler eliminare questa manutenzione? Questa azione non può essere annullata.')) {
      setLoading(true);
      try {
        await api.delete(`/maintenance/${maintenanceId}`);
        setSnackbar({
          open: true,
          message: 'Manutenzione eliminata con successo!',
          severity: 'success'
        });
        closeDialog();
      } catch (err) {
        console.error('Error deleting maintenance:', err);
        setSnackbar({
          open: true,
          message: `Errore durante l'eliminazione della manutenzione: ${err.message}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteWorkSession = async (sessionId) => {
    try {
      await api.delete(`/work-sessions/${sessionId}`);
      setSnackbar({
        open: true,
        message: 'Sessione di lavoro eliminata con successo',
        severity: 'success'
      });
      fetchWorkSessions();
    } catch (err) {
      console.error('Error deleting work session:', err);
      setSnackbar({
        open: true,
        message: 'Errore nell\'eliminazione della sessione di lavoro',
        severity: 'error'
      });
    }
  };

  const handleOpenDepositDialog = async () => {
    await fetchPaymentData();
    setDepositDialog({ open: true });
  };

  const handleCloseDepositDialog = () => {
    setDepositDialog({ open: false });
    setDepositForm({
      amount: '',
      paymentMethodId: '',
      accountId: '',
      paymentDate: new Date().toISOString().split('T')[0]
    });
  };

  const handleDepositFormChange = (field, value) => {
    setDepositForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveDeposit = async () => {
    try {
      const { amount, paymentMethodId, accountId, paymentDate } = depositForm;
      
      // Validazione
      if (!amount || parseFloat(amount) <= 0) {
        setSnackbar({
          open: true,
          message: 'Inserire un importo valido',
          severity: 'error'
        });
        return;
      }
      
      if (!paymentMethodId || !accountId) {
        setSnackbar({
          open: true,
          message: 'Selezionare modalità di pagamento e conto',
          severity: 'error'
        });
        return;
      }

      // Calcola il nuovo acconto (somma dell'acconto esistente + nuovo importo)
      const currentDeposit = parseFloat(maintenance.deposit || 0);
      const newDepositAmount = parseFloat(amount);
      const totalDeposit = currentDeposit + newDepositAmount;

      // Aggiorna la manutenzione con il nuovo acconto
      await api.put(`/maintenance/${maintenanceId}`, {
        ...maintenance,
        deposit: totalDeposit.toFixed(2)
      });

      // Crea il movimento contabile
      await api.post('/accounting-movements', {
        type: 'IN',
        source: 'maintenance',
        sourceId: maintenanceId,
        description: `Acconto aggiuntivo manutenzione #${maintenance.protocolNumber}`,
        amount: newDepositAmount,
        date: paymentDate,
        paymentMethodId: parseInt(paymentMethodId),
        accountId: parseInt(accountId)
      });

      setSnackbar({
        open: true,
        message: 'Acconto aggiunto con successo',
        severity: 'success'
      });
      
      handleCloseDepositDialog();
      fetchMaintenance(); // Ricarica i dati della manutenzione
    } catch (err) {
      console.error('Error saving deposit:', err);
      setSnackbar({
        open: true,
        message: 'Errore nel salvataggio dell\'acconto',
        severity: 'error'
      });
    }
  };

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non specificata';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error(`Data non valida: ${dateString}`);
        return 'Data non valida';
      }
      return format(date, 'dd/MM/yyyy', { locale: it });
    } catch (error) {
      console.error(`Errore formattando la data: ${dateString}`, error);
      return 'Data non valida';
    }
  };

  const handleOpenPrintDialog = () => {
    setPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
  };

  const handlePrintToPdf = async (type) => {
    // Se è preventivo, apri il dialog di selezione
    if (type === 'quote') {
      setPrintDialogOpen(false);
      await fetchQuotesForMaintenance();
      setQuoteSelectionDialogOpen(true);
      return;
    }

    setPrintDialogOpen(false);
    setPrintPreviewOpen(true);
    setSelectedPrintType(type);

    // Attendiamo che il contenuto sia renderizzato
    await new Promise(resolve => setTimeout(resolve, 100));

    const elementId = type === 'service-sheet' ? 'service-sheet' : 'standard-print';
    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Elemento con id ${elementId} non trovato`);
      return;
    }

    try {
      // Calcoliamo le dimensioni in pixel (1mm = 3.779527559055118 px a 96 DPI)
      const pxPerMm = 3.779527559055118;
      const dimensions = type === 'service-sheet' 
        ? { width: 90, height: 205 }
        : { width: 148, height: 210 };

      const canvas = await html2canvas(element, {
        scale: 4,
        useCORS: true,
        logging: false,
        width: dimensions.width * pxPerMm,
        height: dimensions.height * pxPerMm,
        windowWidth: dimensions.width * pxPerMm,
        windowHeight: dimensions.height * pxPerMm
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [dimensions.width, dimensions.height]
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(
        imgData,
        'PNG',
        0,
        0,
        dimensions.width,
        dimensions.height,
        '',
        'FAST'
      );

      pdf.autoPrint();
      window.open(pdf.output('bloburl'), '_blank');
    } catch (error) {
      console.error('Errore durante la generazione del PDF:', error);
      setSnackbar({
        open: true,
        message: 'Errore durante la generazione del PDF',
        severity: 'error'
      });
    }
  };

  const fetchQuotesForMaintenance = async () => {
    if (!maintenanceId) return;
    
    setLoadingQuotes(true);
    try {
      const response = await api.get(`/quotes/maintenance/${maintenanceId}`);
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Errore nel caricamento preventivi:', error);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento dei preventivi',
        severity: 'error'
      });
      setQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleSelectQuote = async (quote) => {
    setLoadingQuotes(true);
    setQuoteSelectionDialogOpen(false);
    
    try {
      // Recupera i dati completi del preventivo
      const response = await api.get(`/quotes/${quote.id}`);
      const fullQuote = response.data;
      
      // Prepara i dati per la generazione del PDF
      const quotePrintData = {
        quote: fullQuote,
        customer: fullQuote.customer || fullQuote.Customer,
        vehicle: fullQuote.vehicle || fullQuote.Vehicle,
        items: fullQuote.items || fullQuote.Items || [],
        maintenanceId: fullQuote.maintenanceId || maintenanceId,
        totals: {
          subtotal: parseFloat(fullQuote.subtotal || 0),
          taxAmount: parseFloat(fullQuote.taxAmount || 0),
          total: parseFloat(fullQuote.total || 0),
          taxRate: 22
        }
      };
      
      // Genera direttamente il PDF senza anteprima
      await generateQuotePdf(quotePrintData);
    } catch (error) {
      console.error('Errore nel recupero dati preventivo:', error);
      setSnackbar({
        open: true,
        message: 'Errore nel recupero dati preventivo',
        severity: 'error'
      });
    } finally {
      setLoadingQuotes(false);
    }
  };

  const generateQuotePdf = async (quotePrintData) => {
    try {
      // Recupera i dati aziendali
      const companyId = localStorage.getItem('companyId');
      const companySettingsResponse = await api.get('/company-settings', {
        headers: { 'x-company-id': companyId }
      });
      const companyData = companySettingsResponse.data;

      const { quote, customer, vehicle, items, totals, maintenanceId: quoteMaintenanceId } = quotePrintData;
      const currentDate = new Date();
      
      // Recupera il numero di manutenzione se disponibile
      let protocolNumber = null;
      const maintId = quote?.maintenanceId || quoteMaintenanceId;
      if (maintId) {
        try {
          const maintenanceResponse = await api.get(`/maintenance/${maintId}`);
          protocolNumber = maintenanceResponse.data?.protocolNumber;
        } catch (err) {
          console.error('Errore nel recupero numero manutenzione:', err);
        }
      }
      
      // Dimensioni A5
      const pageWidth = 148;
      const pageHeight = 210;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });

      // Funzione per aggiungere l'intestazione su ogni pagina
      const addHeader = (pdf, companyData, quoteNumber, protocolNum, pageNum) => {
        // Nome azienda (più compatto)
        pdf.setFontSize(11);
        pdf.setTextColor(219, 17, 17);
        pdf.setFont(undefined, 'bold');
        pdf.text(companyData?.companyName || 'FreeWheel', margin, margin);
        
        // Dati azienda (più compatti)
        pdf.setFontSize(7);
        pdf.setTextColor(80, 80, 80);
        pdf.setFont(undefined, 'normal');
        let yPos = margin + 4;
        
        const addressLine = companyData?.companyAddress ? 
          `${companyData.companyAddress}, ${[companyData?.companyPostalCode, companyData?.companyCity].filter(Boolean).join(' ')}${companyData?.companyProvince ? ` (${companyData.companyProvince})` : ''}` : 
          [companyData?.companyPostalCode, companyData?.companyCity].filter(Boolean).join(' ') + (companyData?.companyProvince ? ` (${companyData.companyProvince})` : '');
        
        if (addressLine) {
          pdf.text(addressLine, margin, yPos);
          yPos += 3;
        }
        
        if (companyData?.companyPhone) {
          pdf.text(`Tel: ${companyData.companyPhone}`, margin, yPos);
          yPos += 3;
        }
        
        // Numero preventivo e manutenzione in alto a destra
        pdf.setFontSize(6);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Preventivo N.:', pageWidth - margin - 25, margin);
        pdf.setFontSize(9);
        pdf.setTextColor(219, 17, 17);
        pdf.setFont(undefined, 'bold');
        pdf.text(quoteNumber || 'N/A', pageWidth - margin - 25, margin + 3);
        
        if (protocolNum) {
          pdf.setFontSize(6);
          pdf.setTextColor(100, 100, 100);
          pdf.text('Manutenzione N.:', pageWidth - margin - 25, margin + 7);
          pdf.setFontSize(9);
          pdf.setTextColor(219, 17, 17);
          pdf.setFont(undefined, 'bold');
          pdf.text(String(protocolNum), pageWidth - margin - 25, margin + 10);
        }
        
        // Linea separatrice (più in alto)
        pdf.setDrawColor(219, 17, 17);
        pdf.setLineWidth(0.3);
        pdf.line(margin, margin + 15, pageWidth - margin, margin + 15);
      };

      // Funzione per aggiungere il footer
      const addFooter = (pdf, pageNum, totalPages) => {
        const footerY = pageHeight - 8;
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Pagina ${pageNum} di ${totalPages}`, pageWidth / 2, footerY, { align: 'center' });
      };

      let currentY = margin + 18;
      let pageNum = 1;
      const footerHeight = 10;
      const minSpaceForItem = 15; // Spazio minimo necessario per un item

      // Aggiungi intestazione prima pagina
      addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);

      // Titolo documento (centrato correttamente)
      pdf.setFontSize(14);
      pdf.setTextColor(219, 17, 17);
      pdf.setFont(undefined, 'bold');
      const titleY = currentY + 4; // Spazio sopra il titolo
      pdf.text('PREVENTIVO', pageWidth / 2, titleY, { align: 'center' });
      currentY = titleY + 6; // Spazio sotto il titolo prima della linea

      // Linea sotto il titolo
      pdf.setDrawColor(219, 17, 17);
      pdf.setLineWidth(0.4);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      // Info Cliente e Veicolo (più compatti, su due colonne)
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'bold');
      
      // Cliente a sinistra
      pdf.text('Cliente:', margin, currentY);
      pdf.setFont(undefined, 'normal');
      pdf.text(`${customer?.firstName || ''} ${customer?.lastName || ''}`, margin + 15, currentY);
      currentY += 3;
      
      if (customer?.phone) {
        pdf.text(`Tel: ${customer.phone}`, margin + 15, currentY);
        currentY += 3;
      }
      
      // Veicolo a destra
      if (vehicle) {
        pdf.setFont(undefined, 'bold');
        pdf.text('Veicolo:', margin + 70, currentY - (customer?.phone ? 6 : 3));
        pdf.setFont(undefined, 'normal');
        const vehicleInfo = [vehicle.brand, vehicle.model].filter(Boolean).join(' ');
        if (vehicleInfo) {
          pdf.text(vehicleInfo, margin + 85, currentY - (customer?.phone ? 6 : 3));
        }
      }
      
      // Data preventivo
      pdf.setFontSize(7);
      pdf.text(`Data: ${format(currentDate, 'dd/MM/yyyy', { locale: it })}`, pageWidth - margin, currentY - 3, { align: 'right' });
      
      currentY += 4;
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.2);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      // Descrizione lavori (più compatta)
      if (quote?.description) {
        pdf.setFontSize(8);
        pdf.setFont(undefined, 'bold');
        pdf.text('Descrizione:', margin, currentY);
        currentY += 3;
        
        pdf.setFontSize(7);
        pdf.setFont(undefined, 'normal');
        const descriptionLines = pdf.splitTextToSize(quote.description, contentWidth);
        descriptionLines.forEach(line => {
          if (currentY > pageHeight - footerHeight - minSpaceForItem) {
            addFooter(pdf, pageNum, 0);
            pdf.addPage();
            pageNum++;
            addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
            currentY = margin + 18;
          }
          pdf.text(line, margin, currentY);
          currentY += 3;
        });
        
        currentY += 3;
      }

      // Tabella prodotti
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'bold');
      pdf.text('DETTAGLIO PREVENTIVO', margin, currentY);
      currentY += 4;

      // Intestazione tabella (colonne meglio allineate)
      pdf.setFontSize(6);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(219, 17, 17);
      pdf.rect(margin, currentY - 3, contentWidth, 5, 'F');
      
      // Colonne: Codice (20mm), Descrizione (60mm), Q.tà (8mm), Prezzo (12mm), Sconto (12mm), Totale (12mm)
      pdf.text('Codice', margin + 1, currentY);
      pdf.text('Descrizione', margin + 22, currentY);
      pdf.text('Q.tà', margin + 83, currentY, { align: 'center' });
      pdf.text('Prezzo', margin + 93, currentY, { align: 'right' });
      pdf.text('Sconto', margin + 106, currentY, { align: 'right' });
      pdf.text('Totale', margin + 126, currentY, { align: 'right' });
      
      currentY += 5;
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 2;

      // Righe prodotti
      pdf.setFontSize(6.5);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      
      // Funzione helper per aggiungere intestazione tabella su nuova pagina
      const addTableHeader = (pdf, yPos) => {
        pdf.setFontSize(6);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.setFillColor(219, 17, 17);
        pdf.rect(margin, yPos - 3, contentWidth, 5, 'F');
        pdf.text('Codice', margin + 1, yPos);
        pdf.text('Descrizione', margin + 22, yPos);
        pdf.text('Q.tà', margin + 83, yPos, { align: 'center' });
        pdf.text('Prezzo', margin + 93, yPos, { align: 'right' });
        pdf.text('Sconto', margin + 106, yPos, { align: 'right' });
        pdf.text('Totale', margin + 126, yPos, { align: 'right' });
        const headerEndY = yPos + 5;
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, headerEndY, pageWidth - margin, headerEndY);
        return headerEndY + 2;
      };
      
      // Assicurati che items sia un array
      const itemsArray = Array.isArray(items) ? items : [];
      
      // Processa tutti gli item
      for (let index = 0; index < itemsArray.length; index++) {
        const item = itemsArray[index];
        const itemTotal = (item.quantity || 0) * parseFloat(item.unitPrice || 0);
        const discountAmount = parseFloat(item.discount) || 0;
        const finalTotal = itemTotal - discountAmount;
        
        // Mostra solo il nome prodotto, non la descrizione
        const productName = item.productName || '';
        
        // Calcola l'altezza necessaria per questo item (solo nome, max 60mm)
        const descLines = pdf.splitTextToSize(productName, 60); // 60mm per la descrizione
        const itemHeight = Math.max(4, descLines.length * 3) + 3; // +3 per spazio extra
        
        // Controlla se serve una nuova pagina PRIMA di disegnare l'item
        if (currentY + itemHeight > pageHeight - footerHeight - 2) {
          addFooter(pdf, pageNum, 0);
          pdf.addPage();
          pageNum++;
          addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
          currentY = addTableHeader(pdf, margin + 18);
          pdf.setFontSize(6.5);
          pdf.setFont(undefined, 'normal');
        }

        // Salva la Y iniziale e la pagina iniziale per questo item (per codice e valori)
        const itemStartY = currentY;
        const itemStartPage = pageNum;
        const lineSpacing = 3.5; // Spaziatura costante tra le righe
        
        // Codice (allargato a 20mm) - solo sulla prima riga
        const codeText = item.productCode || '-';
        pdf.text(codeText, margin + 1, itemStartY);
        
        // Descrizione (solo nome prodotto, max 60mm, può essere su più righe)
        // Usa un'altezza di riga costante per mantenere l'allineamento
        let descCurrentY = itemStartY;
        let descCurrentPage = itemStartPage;
        
        descLines.forEach((line, idx) => {
          if (idx === 0) {
            // Prima riga della descrizione
            pdf.text(line, margin + 22, descCurrentY);
          } else {
            // Calcola la posizione Y per questa riga
            descCurrentY += lineSpacing;
            
            // Controlla se serve nuova pagina durante la descrizione
            if (descCurrentY > pageHeight - footerHeight - 2) {
              addFooter(pdf, descCurrentPage, 0);
              pdf.addPage();
              descCurrentPage++;
              // eslint-disable-next-line no-loop-func
              pageNum = descCurrentPage;
              addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
              descCurrentY = addTableHeader(pdf, margin + 18);
              pdf.setFontSize(6.5);
              pdf.setFont(undefined, 'normal');
            }
            
            // Assicurati di essere sulla pagina corretta
            pdf.setPage(descCurrentPage);
            
            // Disegna la riga della descrizione
            pdf.text(line, margin + 22, descCurrentY);
          }
        });
        
        // Aggiorna currentY e pageNum per la prossima riga
        currentY = descCurrentY + lineSpacing;
        pageNum = descCurrentPage;
        
        // Quantità, Prezzo, Sconto, Totale (sulla prima riga della descrizione - itemStartY)
        // Torna alla pagina iniziale per disegnare i valori
        pdf.setPage(itemStartPage);
        pdf.text(String(item.quantity || 0), margin + 83, itemStartY, { align: 'center' });
        pdf.text(`€${parseFloat(item.unitPrice || 0).toFixed(2)}`, margin + 93, itemStartY, { align: 'right' });
        pdf.text(discountAmount > 0 ? `€${discountAmount.toFixed(2)}` : '-', margin + 106, itemStartY, { align: 'right' });
        pdf.text(`€${finalTotal.toFixed(2)}`, margin + 126, itemStartY, { align: 'right' });
        
        // Torna alla pagina corrente per continuare
        pdf.setPage(pageNum);
        pdf.setDrawColor(240, 240, 240);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 1;
      }

      currentY += 4;

      // Totali
      if (currentY > pageHeight - footerHeight - 15) {
        addFooter(pdf, pageNum, 0);
        pdf.addPage();
        pageNum++;
        addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
        currentY = margin + 18;
      }

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      pdf.text('Subtotale:', pageWidth - margin - 40, currentY, { align: 'right' });
      pdf.text(`€${(totals?.subtotal || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 4;

      pdf.text(`IVA (${totals?.taxRate || 22}%) inclusa:`, pageWidth - margin - 40, currentY, { align: 'right' });
      pdf.text(`€${(totals?.taxAmount || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 6;

      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(pageWidth - margin - 40, currentY, pageWidth - margin, currentY);
      currentY += 4;

      pdf.setFontSize(11);
      pdf.setTextColor(219, 17, 17);
      pdf.setFont(undefined, 'bold');
      pdf.text('TOTALE:', pageWidth - margin - 40, currentY, { align: 'right' });
      pdf.text(`€${(totals?.total || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });

      // Aggiungi footer all'ultima pagina
      const totalPages = pageNum;
      // Rimuovi il footer precedente se esiste e aggiungi quello corretto
      pdf.setPage(pageNum);
      addFooter(pdf, pageNum, totalPages);

      // Aggiungi footer a tutte le pagine precedenti
      for (let i = 1; i < pageNum; i++) {
        pdf.setPage(i);
        addFooter(pdf, i, totalPages);
      }

      // Torna all'ultima pagina
      pdf.setPage(pageNum);

      // Genera il PDF come blob e aprilo direttamente
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Preventivo_${quote?.quoteNumber || 'N/A'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Errore durante la generazione del PDF preventivo:', error);
      setSnackbar({
        open: true,
        message: 'Errore durante la generazione del PDF',
        severity: 'error'
      });
    }
  };

  // Prepara i dati della manutenzione per la stampa
  const getMaintenancePrintData = () => {
    if (!maintenance) return null;
    
    return {
      customerName: maintenance.Customer 
        ? `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}` 
        : '',
      customerPhone: maintenance.Customer?.phone || '',
      bikeBrand: maintenance.Vehicle?.brand || '',
      bikeModel: maintenance.Vehicle?.model || '',
      description: maintenance.description || '',
      deposit: maintenance.deposit || '0',
      estimatedCost: maintenance.estimatedCost || maintenance.finalCost || '0',
      protocolNumber: maintenance.protocolNumber || '',
      maintenance: maintenance
    };
  };

  if (!maintenance) {
    return null;
  }

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        Dettagli Manutenzione - Protocollo #{maintenance.protocolNumber}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Customer and Vehicle Info */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Cliente: {maintenance.Customer ? `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}` : 'N/D'}
                  {maintenance.Customer?.phone && (
                    <>
                      {` - ${maintenance.Customer.phone}`}
                      {maintenance.Customer?.id && (
                        <Tooltip title="Visualizza storico manutenzioni">
                          <IconButton
                            size="small"
                            onClick={handleOpenHistoryDialog}
                            color="primary"
                            sx={{ ml: 1, verticalAlign: 'middle' }}
                          >
                            <BuildIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Veicolo: {maintenance.Vehicle ? `${maintenance.Vehicle.brand} ${maintenance.Vehicle.model}` : 'N/D'}
                </Typography>
                <Typography variant="subtitle1">
                  Problema: {maintenance.description}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Data: {formatDate(maintenance.startDate)}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Costo: {maintenance.finalCost ? 
                    `€${parseFloat(maintenance.finalCost).toFixed(2)}` : 
                    maintenance.estimatedCost ? 
                    `€${parseFloat(maintenance.estimatedCost).toFixed(2)} (Stimato)` : 
                    'Non specificato'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="subtitle1" sx={{ mr: 1 }}>
                    Stato:
                  </Typography>
                  <Chip 
                    label={maintenance.status} 
                    color={maintenance.status === 'Consegnato' ? 'success' : 'primary'} 
                    size="small" 
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Total Cost */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1">
                      Importo Totale da Pagare:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EuroIcon sx={{ mr: 1 }} />
                      <Typography variant="h5">
                        {maintenance.finalCost ? parseFloat(maintenance.finalCost).toFixed(2) : '0.00'} €
                      </Typography>
                    </Box>
                  </Box>
                  
                  {parseFloat(maintenance.discount || 0) > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1">
                        Sconto:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EuroIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        <Typography variant="h6" sx={{ color: 'success.light' }}>
                          -{parseFloat(maintenance.discount || 0).toFixed(2)} €
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1">
                      Acconto:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EuroIcon sx={{ mr: 1, fontSize: '1rem' }} />
                      <Typography variant="h6">
                        {maintenance.deposit ? parseFloat(maintenance.deposit).toFixed(2) : '0.00'} €
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleOpenDepositDialog}
                        sx={{ 
                          ml: 1, 
                          bgcolor: 'success.main', 
                          color: 'white',
                          '&:hover': { 
                            bgcolor: 'success.dark',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                        title="Aggiungi acconto"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.2)', pt: 2 }}>
                    <Typography variant="h6">
                      Saldo da Pagare:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EuroIcon sx={{ mr: 1 }} />
                      <Typography variant="h4">
                        {(
                          parseFloat(maintenance.finalCost || 0) - 
                          parseFloat(maintenance.discount || 0) - 
                          parseFloat(maintenance.deposit || 0)
                        ).toFixed(2)} €
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            {/* Materials and Labor */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Lavorazioni e Materiali Utilizzati
                </Typography>
                <List>
                  {maintenanceItems.length === 0 ? (
                    <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                      Nessun articolo selezionato
                    </Typography>
                  ) : (
                    maintenanceItems.map((item) => (
                      <React.Fragment key={item.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ color: '#000000' }}>
                                  {item.customItem 
                                    ? `${item.customCode || 'ART-CUST'} - ${item.customDescription}`
                                    : `${item.Product?.code || ''} - ${item.Product?.name || 'Prodotto non disponibile'}`
                                  }
                                </Typography>
                                {item.isLabor && (
                                  <Chip 
                                    label="Manodopera" 
                                    color="primary" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {item.customItem && (
                                  <Chip 
                                    label="Personalizzato" 
                                    color="secondary" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={`Quantità: ${item.quantity} - Prezzo: €${(parseFloat(item.unitPrice) * item.quantity).toFixed(2)}`}
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))
                  )}
                </List>
              </Paper>
            </Grid>
            
            {/* Work Sessions */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Sessioni di Manodopera
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" color="primary.main">
                      Tempo Totale: {formatTime(calculateTotalLaborTime())}
                    </Typography>
                  </Box>
                </Box>
                
                {workSessions.length === 0 ? (
                  <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                    Nessuna sessione di lavoro registrata
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {workSessions.map((session) => {
                      const startTime = new Date(session.startTime);
                      const endTime = session.endTime ? new Date(session.endTime) : null;
                      
                      return (
                        <Card key={session.id} sx={{ mb: 1 }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 1, bgcolor: 'primary.main', width: 32, height: 32 }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="subtitle2">
                                  {session.Mechanic?.User?.name || 'Meccanico'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  icon={<TimerIcon fontSize="small" />} 
                                  label={formatTime(session.duration || 0)} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteWorkSession(session.id)}
                                  title="Elimina sessione"
                                  sx={{ ml: 1, bgcolor: 'rgba(211, 47, 47, 0.1)' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              {startTime.toLocaleString()} - {endTime ? endTime.toLocaleString() : 'In corso'}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Private Notes */}
            {maintenance?.privateNotes && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Note Private
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1, 
                    border: '1px solid', 
                    borderColor: 'grey.300' 
                  }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {maintenance.privateNotes}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Cost Report */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Report Costi
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Costo Manodopera
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(calculateTotalLaborTime())} ({(calculateTotalLaborTime() / 3600).toFixed(2)} ore)
                        </Typography>
                        <Typography variant="h6" color="primary">
                          €{laborCost.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Costo Materiali (Acquisto + IVA 22%)
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {maintenanceItems.filter(p => !p.isLabor).length} articoli
                        </Typography>
                        <Typography variant="h6" color="primary">
                          €{calculateMaterialsCost().toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Spese Generali (20% + IVA 22%)
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Typography variant="h6" color="primary">
                          €{calculateOverheadCost().toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                      <Typography variant="subtitle1" gutterBottom color="inherit">
                        Totale Complessivo (Costo)
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Typography variant="h5" color="inherit">
                          €{calculateGrandTotal().toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Profit Section - usa Importo Totale da Pagare (scontato), non prezzo di listino */}
                <Box sx={{ 
                  mt: 3, 
                  p: 2, 
                  border: '2px solid', 
                  borderColor: 'success.main', 
                  borderRadius: 1, 
                  bgcolor: 'success.light',
                  color: 'success.contrastText'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                      Con questa manutenzione hai Guadagnato:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EuroIcon sx={{ mr: 1 }} />
                      <Typography variant="h4">
                        {(
                          parseFloat(maintenance.finalCost || 0) - 
                          parseFloat(maintenance.discount || 0) - 
                          calculateGrandTotal()
                        ).toFixed(2)} €
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={closeDialog}>Chiudi</Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteMaintenance}
          >
            Elimina
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handleOpenPrintDialog}
          >
            Stampa
          </Button>
          {(maintenance.status === 'Consegnato' || maintenance.status === 'Da Ritirare' || maintenance.status === 'Sollecitato') ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<BuildIcon />}
              onClick={handleReopenMaintenance}
            >
              Riapri Manutenzione
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              onClick={handleSendWhatsApp}
              sx={{ display: maintenance.status === 'Da Chiamare' ? 'flex' : 'none' }}
            >
              Invia WhatsApp
            </Button>
          )}
        </Box>
      </DialogActions>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* WhatsApp Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => handleConfirmWhatsApp(false)}
        aria-labelledby="whatsapp-confirmation-dialog-title"
        aria-describedby="whatsapp-confirmation-dialog-description"
      >
        <DialogTitle id="whatsapp-confirmation-dialog-title">
          Conferma invio WhatsApp
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="whatsapp-confirmation-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmWhatsApp(false)} color="primary">
            No
          </Button>
          <Button onClick={() => handleConfirmWhatsApp(true)} color="primary" variant="contained" autoFocus>
            Sì, messaggio inviato
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per aggiungere acconto */}
      <Dialog
        open={depositDialog.open}
        onClose={handleCloseDepositDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Aggiungi Acconto</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Acconto attuale: €{maintenance?.deposit ? parseFloat(maintenance.deposit).toFixed(2) : '0.00'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Importo Acconto"
                type="number"
                value={depositForm.amount}
                onChange={(e) => handleDepositFormChange('amount', e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>€</Typography>,
                }}
                inputProps={{
                  min: 0.01,
                  step: 0.01
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Modalità di Pagamento</InputLabel>
                <Select
                  value={depositForm.paymentMethodId}
                  onChange={(e) => handleDepositFormChange('paymentMethodId', e.target.value)}
                  label="Modalità di Pagamento"
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Conto</InputLabel>
                <Select
                  value={depositForm.accountId}
                  onChange={(e) => handleDepositFormChange('accountId', e.target.value)}
                  label="Conto"
                >
                  {accounts.map(account => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Data Pagamento"
                type="date"
                value={depositForm.paymentDate}
                onChange={(e) => handleDepositFormChange('paymentDate', e.target.value)}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDepositDialog}>Annulla</Button>
          <Button 
            onClick={handleSaveDeposit}
            variant="contained"
            disabled={!depositForm.amount || !depositForm.paymentMethodId || !depositForm.accountId}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per selezionare il tipo di stampa */}
      <Dialog open={printDialogOpen} onClose={handleClosePrintDialog}>
        <DialogTitle>Seleziona il tipo di stampa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Scegli il formato di stampa desiderato:
          </DialogContentText>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handlePrintToPdf('service-sheet')}
              sx={{ py: 1.5 }}
            >
              Scheda di lavoro
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handlePrintToPdf('standard')}
              sx={{ py: 1.5 }}
            >
              Standard
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handlePrintToPdf('quote')}
              sx={{ py: 1.5 }}
            >
              Preventivo
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog}>Annulla</Button>
        </DialogActions>
      </Dialog>

      {/* Preview per la stampa */}
      <Dialog
        open={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Anteprima Stampa</DialogTitle>
        <DialogContent>
          {selectedPrintType === 'service-sheet' && getMaintenancePrintData() && (
            <MaintenanceServiceSheet
              maintenance={getMaintenancePrintData()}
              nextProtocolNumber={maintenance.protocolNumber}
            />
          )}
          {selectedPrintType === 'standard' && getMaintenancePrintData() && (
            <MaintenanceStandardPrint
              maintenance={getMaintenancePrintData()}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintPreviewOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per selezionare il preventivo */}
      <Dialog
        open={quoteSelectionDialogOpen}
        onClose={() => setQuoteSelectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Seleziona Preventivo da Stampare</DialogTitle>
        <DialogContent>
          {loadingQuotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : quotes.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
              Nessun preventivo disponibile per questa manutenzione
            </Typography>
          ) : (
            <List>
              {quotes.map((quote) => (
                <React.Fragment key={quote.id}>
                  <ListItem
                    button
                    onClick={() => handleSelectQuote(quote)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="h6">
                          {quote.quoteNumber || `Preventivo #${quote.id}`}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {quote.title || quote.description || 'Preventivo'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Totale: €{parseFloat(quote.total || 0).toFixed(2)}
                          </Typography>
                          {quote.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(quote.createdAt)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteSelectionDialogOpen(false)}>Annulla</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance History Dialog */}
      <Dialog open={openHistoryDialog} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6" component="span" sx={{ flex: 1 }}>
              Storico Manutenzioni - {maintenance?.Customer ? `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}` : ''}
            </Typography>
            <IconButton
              onClick={() => {
                if (maintenance?.Customer?.id) {
                  fetchMaintenanceHistory(maintenance.Customer.id);
                }
              }}
              color="primary"
              disabled={loadingHistory}
              size="small"
              sx={{ ml: 2 }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : maintenanceHistory.length > 0 ? (
            <List>
              {maintenanceHistory.map((histMaintenance) => (
                <ListItem 
                  key={histMaintenance.id} 
                  button
                  onClick={() => handleMaintenanceClick(histMaintenance.id)}
                  divider
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1">
                          Protocollo #{histMaintenance.protocolNumber}
                        </Typography>
                        <Chip 
                          label={histMaintenance.status} 
                          color={getStatusColorHistory(histMaintenance.status)} 
                          size="small" 
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Data: {formatDateHistory(histMaintenance.createdAt)}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span">
                          {histMaintenance.description && histMaintenance.description.length > 0
                            ? histMaintenance.description.substring(0, 100) + (histMaintenance.description.length > 100 ? '...' : '')
                            : 'Nessuna descrizione'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              Nessuna manutenzione trovata per questo cliente
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance Details Dialog (nested) */}
      {selectedMaintenanceId && (
        <MaintenanceDetails
          open={openMaintenanceDetails}
          onClose={handleCloseMaintenanceDetails}
          maintenanceId={selectedMaintenanceId}
        />
      )}
    </Dialog>
  );
};

export default MaintenanceDetails;